import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import MenuItemForm from './MenuItemForm';
import { apiFetch } from '../lib/apiClient';

export default function MenuManager({ truck }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchMenu();
  }, [truck.truck_id]);

  const fetchMenu = async () => {
    try {
      const response = await apiFetch(`/api/trucks/${truck.truck_id}/menu`);
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Failed to fetch menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAvailability = async (item) => {
    try {
      const response = await apiFetch(`/api/menu/${item.item_id}/availability`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ available: !item.available }),
      });

      if (response.ok) {
        setItems(prev => prev.map(i => 
          i.item_id === item.item_id ? { ...i, available: !i.available } : i
        ));
        toast.success(item.available ? 'Item marked as sold out' : 'Item is now available');
      }
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await apiFetch(`/api/menu/${itemId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setItems(prev => prev.filter(i => i.item_id !== itemId));
        toast.success('Item deleted');
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleItemSaved = (savedItem) => {
    if (editingItem) {
      setItems(prev => prev.map(i => i.item_id === savedItem.item_id ? savedItem : i));
    } else {
      setItems(prev => [...prev, savedItem]);
    }
    setShowForm(false);
    setEditingItem(null);
  };

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    const category = item.category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-card rounded-xl p-4 animate-pulse border">
            <div className="h-5 bg-muted rounded w-1/3 mb-2" />
            <div className="h-4 bg-muted rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Menu Items</h2>
        <Button onClick={() => setShowForm(true)} data-testid="add-menu-item-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-card rounded-xl p-8 text-center border">
          <p className="text-muted-foreground mb-4">No menu items yet</p>
          <Button onClick={() => setShowForm(true)}>Add Your First Item</Button>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedItems).map(([category, categoryItems]) => (
            <div key={category}>
              <h3 className="font-semibold text-muted-foreground mb-3">{category}</h3>
              <div className="space-y-3">
                {categoryItems.map(item => (
                  <div 
                    key={item.item_id}
                    className="bg-card rounded-xl border overflow-hidden"
                    data-testid={`menu-manager-item-${item.item_id}`}
                  >
                    <div className="flex items-center">
                      {item.photo_url && (
                        <img 
                          src={item.photo_url} 
                          alt={item.name}
                          className="w-20 h-20 object-cover"
                        />
                      )}
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{item.name}</h4>
                              {!item.available && (
                                <Badge variant="secondary">Sold Out</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {item.description}
                            </p>
                          </div>
                          <span className="font-semibold text-primary">
                            CA$ {item.price.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 px-4">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleToggleAvailability(item)}
                          title={item.available ? 'Mark as sold out' : 'Mark as available'}
                        >
                          {item.available ? (
                            <ToggleRight className="w-5 h-5 text-green-600" />
                          ) : (
                            <ToggleLeft className="w-5 h-5 text-muted-foreground" />
                          )}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => { setEditingItem(item); setShowForm(true); }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(item.item_id)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <MenuItemForm 
          truckId={truck.truck_id}
          item={editingItem}
          onClose={() => { setShowForm(false); setEditingItem(null); }}
          onSaved={handleItemSaved}
        />
      )}
    </div>
  );
}
